<template>
  <div>
    <!-- The Clickable Status Input Box in the Filter Bar -->
    <div
      class="form-control status-trigger-box d-flex align-items-center justify-content-between cursor-pointer"
      @click="openModal"
    >
      <div class="d-flex align-items-center flex-nowrap overflow-hidden tags-wrapper">
        <template v-if="value && value.length > 0">
          <!-- Display up to 2 badges -->
          <span
            v-for="statusKey in visibleSelectedStatuses"
            :key="statusKey"
            :class="['badge status-pill text-truncate mr-25 flex-shrink-1', statusPillClass]"
            @click.stop
          >
            <span class="pill-text">{{ getStatusName(statusKey) }}</span>
            <feather-icon
              icon="XIcon"
              size="12"
              class="cursor-pointer ml-25 flex-shrink-0"
              @click.stop="removeStatus(statusKey)"
            />
          </span>

          <!-- Remaining count badge (+N more) -->
          <span
            v-if="remainingCount > 0"
            class="badge status-overflow-pill mr-50 mr-25 flex-shrink-0"
          >
            +{{ remainingCount }} more
          </span>
        </template>
        <span
          v-else
          class="placeholder-text text-nowrap"
        >
          Select Status
        </span>
      </div>

      <!-- Right chevron arrow -->
      <feather-icon
        icon="ChevronDownIcon"
        size="16"
        class="flex-shrink-0 mx-1"
      />
    </div>

    <!-- The Selection Modal -->
    <b-modal
      :id="modalId"
      ref="statusModal"
      title="Select Statuses"
      size="lg"
      centered
      hide-footer
      no-close-on-backdrop
      dialog-class="status-modal-dialog"
      modal-class="status-filter-modal-dialog-wrapper"
    >
      <div>
        <!-- Search Bar -->
        <b-input-group class="mb-1 search-input-group">
          <b-input-group-prepend is-text>
            <feather-icon
              icon="SearchIcon"
              size="14"
              class="search-icon"
            />
          </b-input-group-prepend>
          <b-form-input
            v-model="searchQuery"
            placeholder="Search"
            trim
          />
        </b-input-group>

        <!-- Selected Statuses Bar Inside Modal -->
        <div
          v-if="tempSelected.length > 0"
          class="mb-1 selected-status-bar"
        >
          <div class="font-small-3 selected-count mb-50">
            {{ tempSelected.length }} selected
          </div>
          <div class="d-flex flex-wrap align-items-center">
            <span
              v-for="statusKey in tempSelected.slice(0, 5)"
              :key="`modal-tag-${statusKey}`"
              class="badge status-pill mr-50 mt-50"
            >
              <span class="pill-text">{{ getStatusName(statusKey) }}</span>
            </span>
            <span
              v-if="tempSelected.length > 5"
              class="badge status-overflow-pill mt-50"
            >
              +{{ tempSelected.length - 5 }} more
            </span>
          </div>
        </div>

        <!-- 2-Column Checkbox List -->
        <div class="checkbox-container">
          <b-row>
            <b-col
              v-for="status in filteredStatuses"
              :key="status.key"
              cols="12"
              md="6"
              class="mb-75"
            >
              <b-form-checkbox
                :checked="tempSelected.includes(status.key)"
                class="custom-checkbox-status"
                @change="toggleStatus(status.key)"
              >
                {{ status.name }}
              </b-form-checkbox>
            </b-col>
          </b-row>

          <div
            v-if="filteredStatuses.length === 0"
            class="text-center text-muted my-2"
          >
            No status found matching "{{ searchQuery }}"
          </div>
        </div>
        <div class="w-100 mt-2 mb-1 d-flex justify-content-center align-items-center">
          <b-button
            variant="primary"
            class="px-3 mr-1"
            @click="applySelection"
          >
            Submit
          </b-button>
          <b-button
            variant="outline-secondary"
            class="px-3"
            @click="clearSelection"
          >
            Clear
          </b-button>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
import {
  BModal,
  BButton,
  BFormInput,
  BInputGroup,
  BInputGroupPrepend,
  BFormCheckbox,
  BRow,
  BCol,
} from 'bootstrap-vue'
import FeatherIcon from '@/@core/components/feather-icon/FeatherIcon.vue'

export default {
  name: 'StatusFilterModal',
  components: {
    BModal,
    BButton,
    BFormInput,
    BInputGroup,
    BInputGroupPrepend,
    BFormCheckbox,
    BRow,
    BCol,
    FeatherIcon,
  },
  props: {
    modalId: {
      type: String,
      default: () => `status-filter-modal-${Math.random().toString(36).substr(2, 9)}`,
    },
    value: {
      type: Array,
      default: () => [],
    },
    statusOptions: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      searchQuery: '',
      tempSelected: [],
    }
  },
  computed: {
    visibleSelectedStatuses() {
      return this.value.slice(0, 2)
    },
    remainingCount() {
      return Math.max(0, this.value.length - 2)
    },
    statusPillClass() {
      if (this.value.length === 1) {
        return 'single-pill'
      }
      if (this.remainingCount > 0) {
        return 'multi-pill-with-overflow'
      }
      return 'multi-pill'
    },
    filteredStatuses() {
      if (!this.searchQuery) return this.statusOptions
      const q = this.searchQuery.toLowerCase().trim()
      return this.statusOptions.filter(s => s.name && s.name.toLowerCase().includes(q))
    },
  },
  methods: {
    openModal() {
      this.tempSelected = [...this.value]
      this.searchQuery = ''
      this.$refs.statusModal.show()
    },
    closeModal() {
      this.$refs.statusModal.hide()
    },
    getStatusName(key) {
      const found = this.statusOptions.find(o => o.key === key)
      return found ? found.name : key
    },
    toggleStatus(key) {
      const idx = this.tempSelected.indexOf(key)
      if (idx > -1) {
        this.tempSelected.splice(idx, 1)
      } else {
        this.tempSelected.push(key)
      }
    },
    removeStatus(key) {
      const updated = this.value.filter(k => k !== key)
      this.$emit('input', updated)
      this.$emit('change', updated)
    },
    applySelection() {
      this.$emit('input', [...this.tempSelected])
      this.$emit('change', [...this.tempSelected])
      this.closeModal()
    },
    clearSelection() {
      this.tempSelected = []
    },
  },
}
</script>

<style lang="scss" scoped>
.status-trigger-box {
  min-height: 38px;
  height: auto;
  padding: 4px 10px;
  background-color: #fff;
  border: 1px solid #d8d6de;
  border-radius: 0.357rem;
  cursor: pointer;

  .tags-wrapper {
    max-width: calc(100% - 24px);
  }

  .placeholder-text {
    font-size: 1rem;
    padding-left: 7px;
  }
}

.search-input-group {
  ::v-deep {
    .input-group-text {
      background-color: #fff;
      border-right: none;
      padding-right: 4px;
      padding-left: 10px;
    }

    .form-control {
      border-left: none;
      padding-left: 4px;

      &:focus {
        border-color: #d8d6de;
        box-shadow: none;
      }
    }

    .search-icon {
      color: #6e6b7b;
    }
  }

  &:focus-within ::v-deep {
    .input-group-text,
    .form-control {
      border-color: var(--primary) !important;
    }
  }
}

.selected-status-bar {
  background-color: #F6F7F9;
  border-radius: 5px;
  padding: 5px 10px;
}

.selected-count {
  font-weight: bolder;
}

.status-pill {
  background-color: var(--primary) !important;
  color: #ffffff !important;
  font-weight: 500;
  font-size: 0.78rem;
  padding: 4px 8px;
  border-radius: 4px;
  display: inline-flex;
  align-items: center;
  max-width: 130px;

  .pill-text {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  svg {
    stroke-width: 2.5;
  }

  &.single-pill {
    max-width: 100%;
  }

  &.multi-pill {
    max-width: calc(50% - 6px);
  }

  &.multi-pill-with-overflow {
    max-width: calc(38% - 6px);
  }
}

.status-overflow-pill {
  background-color: var(--primary) !important;
  color: #ffffff !important;
  font-weight: 600;
  font-size: 0.78rem;
  padding: 4px 8px;
  border-radius: 4px;
}

.checkbox-container {
  max-height: 380px;
  overflow-y: auto;
  overflow-x: hidden;
  padding-right: 5px;
  width: 100%;
}

.custom-checkbox-status {
  ::v-deep .custom-control-label {
    font-size: 0.9rem;
    color: #4b4b4b;
    cursor: pointer;
    line-height: 1.4;
  }
}
</style>

<style lang="scss">
/* Stacking Layer Fix (ensures this modal always appears above parent modals like EditSavedFilterModal) */
.modal.status-filter-modal-dialog-wrapper {
  z-index: 1060 !important;

  .modal-dialog {
    z-index: 1061 !important;
  }
}

/* Elevate the modal outer container and backdrop when status modal is open */
.modal.status-filter-modal-dialog-wrapper ~ .modal-backdrop {
  z-index: 1055 !important;
}
</style>
